import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react";

type Lang = "en" | "ar";

interface LanguageContextType {
  lang: Lang;
  toggleLang: () => void;
  t: (en: string, ar: string) => string;
  isRtl: boolean;
}

const LanguageContext = createContext<LanguageContextType | null>(null);

function getInitialLang(): Lang {
  try {
    const stored = localStorage.getItem("bella-rose-lang");
    if (stored === "ar" || stored === "en") return stored;
  } catch {}
  return "en";
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLang] = useState<Lang>(getInitialLang);

  // Persist language choice and update document direction
  useEffect(() => {
    try {
      localStorage.setItem("bella-rose-lang", lang);
    } catch {}
    // Update document-level attributes for accessibility and CSS
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  }, [lang]);

  const toggleLang = useCallback(() => {
    setLang((prev) => (prev === "en" ? "ar" : "en"));
  }, []);

  const t = useCallback(
    (en: string, ar: string) => (lang === "en" ? en : ar),
    [lang]
  );

  const isRtl = lang === "ar";

  return (
    <LanguageContext.Provider value={{ lang, toggleLang, t, isRtl }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLanguage must be used within LanguageProvider");
  return ctx;
}
